import React, { useState, useRef, useEffect } from "react";
import Icon from "../../../components/AppIcon";

const ChatInput = ({
  onCommand,
  commandHistory = [],
  isProcessing = false,
  placeholder = "System’s up! Need a hand? Just type help.",
  autoComplete = [],
}) => {
  const [input, setInput] = useState("");
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (input?.trim() && autoComplete?.length > 0) {
      const filtered = autoComplete?.filter((cmd) =>
        cmd?.toLowerCase()?.startsWith(input?.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(filtered?.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [input, autoComplete]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (input?.trim() && !isProcessing) {
      onCommand(input?.trim());
      setInput("");
      setHistoryIndex(-1);
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === "ArrowUp") {
      e?.preventDefault();
      if (commandHistory?.length > 0) {
        const newIndex = historyIndex + 1;
        if (newIndex < commandHistory?.length) {
          setHistoryIndex(newIndex);
          setInput(commandHistory?.[commandHistory?.length - 1 - newIndex]);
        }
      }
    } else if (e?.key === "ArrowDown") {
      e?.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory?.[commandHistory?.length - 1 - newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput("");
      }
    } else if (e?.key === "Tab") {
      e?.preventDefault();
      if (suggestions?.length > 0) {
        setInput(suggestions?.[0]);
        setShowSuggestions(false);
      }
    } else if (e?.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    setShowSuggestions(false);
    inputRef?.current?.focus();
  };

  return (
    <div className="relative">
      {/* Auto-complete suggestions */}
      {showSuggestions && (
        <div className="absolute bottom-full left-0 mb-2 bg-card border border-terminal-muted rounded-md shadow-lg z-10 min-w-48">
          {suggestions?.map((suggestion, index) => (
            <div
              key={index}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-primary/10 hover:text-primary transition-colors duration-200 font-fira"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className="flex items-center space-x-3 p-3 bg-card border border-border rounded-full"
      >
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e?.target?.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || "Type a message..."}
            disabled={isProcessing}
            className="w-full bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground disabled:opacity-50 py-2 px-1 focus:outline-none focus:border-none focus:ring-0"
            autoComplete="off"
            spellCheck="false"
          />
        </div>

        {/* Send button */}
        <button
          type="submit"
          disabled={!input.trim() || isProcessing}
          className="flex items-center justify-center w-8 h-8 rounded-full bg-primary hover:bg-primary/90 disabled:bg-muted disabled:cursor-not-allowed transition-colors duration-200"
        >
          {isProcessing ? (
            <Icon
              name="Loader2"
              size={16}
              className="text-primary-foreground animate-spin"
            />
          ) : (
            <Icon name="Send" size={16} className="text-primary-foreground" />
          )}
        </button>
      </form>
    </div>
  );
};

export default ChatInput;
